import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  readLastVisited,
  writeLastVisited,
  type ExplorerSurface,
  type LastVisitedSelection
} from './last-visited'
import type { ExplorerRouteSelection } from './useExplorerSelection'

interface UseExplorerLastVisitedOptions {
  surface: ExplorerSurface
  /** Selection read from the route by `useExplorerSelection`. */
  selection: ExplorerRouteSelection
  /** Folders + items have loaded, so a remembered path can be verified. */
  ready: boolean
  /**
   * Another effect owns the initial navigation (legacy `?dashboard=` /
   * `?template=` / `?folder=` / `?view=` migration). Restoring underneath it
   * would race that redirect.
   */
  paused?: boolean
  /**
   * Resolve a remembered selection to the href to restore, or `null` when the
   * folder / item no longer exists.
   */
  hrefFor: (selection: LastVisitedSelection) => string | null
}

/**
 * Persist the explorer selection and, on the first landing of a mount, reopen
 * the remembered one when the route carries no selection (AXI-5324).
 *
 * The restore fires at most once per mount, so it only covers *arriving* at a
 * surface home — navigating home from within the page (deleting the open
 * dashboard, clicking the already-active nav entry) still lands on the empty
 * state instead of bouncing the user back.
 */
export function useExplorerLastVisited({
  surface,
  selection,
  ready,
  paused = false,
  hrefFor
}: UseExplorerLastVisitedOptions): void {
  const navigate = useNavigate()
  const landingSettledRef = useRef(false)

  const kind = selection.kind
  const path = selection.kind === 'none' ? '' : selection.path

  useEffect(() => {
    if (kind === 'none') return
    writeLastVisited(surface, { kind, path })
  }, [surface, kind, path])

  useEffect(() => {
    if (landingSettledRef.current || !ready || paused) return
    landingSettledRef.current = true
    if (kind !== 'none') return
    const remembered = readLastVisited(surface)
    if (remembered === null) return
    const href = hrefFor(remembered)
    if (href === null) return
    navigate(href, { replace: true })
  }, [surface, kind, ready, paused, hrefFor, navigate])
}
